<template>
  <div class="mx-10 ma-5">
    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="onDismissed" :text="errors"></Alert>
      </v-flex>
    </v-layout>
    <div>
      <v-card flat class="pa-5">
        <PageTitleWithSearch
          :title="'Product Categories'"
          :btnName="'Add New Category'"
          :btnColor="'#00B3FF'"
          :btnRoute="'/product-category/create'"
          :btnPermission="'product.create'"
          :btnEnable="true"
          :loading="loading"
          :search="search"
          @searchText="searchChange($event)"
        />
        <DataTableWithPagination
          :headers="headers"
          :items="categories"
          :loading="loading"
          :page="page"
          :total="total"
          @pageChange="page = $event"
        >
          <template #row="{ item}">
            <tr :key="item.uuid">
              <td class="text-left">{{ item.code }}</td>
              <td class="text-left">{{ item.name }}</td>
              <td class="text-center">
                <v-btn
                  v-if="hasPermission('product.edit')"
                  class="ma-1"
                  color="#FFE0B2"
                  :to="'/product-category/edit/' + item.uuid"
                  fab
                  x-small
                  style="box-shadow: none;"
                >
                  <v-icon small color="#FF9800">mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  v-else
                  class="ma-1"
                  disabled
                  fab
                  x-small
                  style="box-shadow: none;"
                >
                  <v-icon small color="#FF9800">mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  v-if="hasPermission('product.delete')"
                  color="#FFCDD2"
                  fab
                  x-small
                  style="box-shadow: none;"
                  @click.stop="dropCategoryConfirm(item.uuid)"
                >
                  <v-icon small color="#F44336">mdi-delete</v-icon>
                </v-btn>
                <v-btn v-else disabled fab x-small style="box-shadow: none;">
                  <v-icon small color="#FF9800">mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
        </DataTableWithPagination>
      </v-card>
    </div>
    <ConfirmModal
      :text="'Do you want to drop this product category?'"
      :title="'Drop Product Category'"
      :color="'red'"
      :confirmOpen="confirmOpen"
      @cancelConfirm="cancelConfirm"
      @confirm="confirm"
    />
    <Snackbars
      :text="successMsg"
      :snackbar="snackbar"
      :color="snackbarColor"
      @classSnackbar="closeSnackbar"
    />
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");

import PageTitleWithSearch from "@/components/shared/PageTitleWithSearch";
import ConfirmModal from "@/components/shared/ConfirmModal";
import Snackbars from "@/components/shared/Snackbars";
import DataTableWithPagination from "@/components/shared/DataTableWithPagination.vue";
import { checkError } from "@/config/errors.js";
import { ProductCategoryService } from "../../services/productCategory-service";
import Alert from "@/components/shared/Alert.vue";

export default {
  name: "ProductCategoryGrid",
  components: {
    PageTitleWithSearch,
    ConfirmModal,
    Snackbars,
    DataTableWithPagination,
    Alert,
  },
  data() {
    return {
      products: [],
      page: 1,
      search: "",
      loading: false,
      role: localStorage.getItem("role") || null,
      total: 0,
      totalProducts: 0,
      dropId: null,
      confirmOpen: false,
      errors: null,
      snackbar: false,
      successMsg: "",
      snackbarColor: "warning",
      headers: [
        { text: "Category Code", value: "category_code", align: "start" },
        { text: "Category Name", value: "name", align: "start" },
        { text: "Action", value: "actions", align: "center" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      profile: "profile",
      hasPermission: "hasPermission",
    }),
  },
  mounted() {
    this.$store.dispatch("auth/getProfile");
    this.getCategories();
  },
  watch: {
    search(newSearch) {
      this.getCategories(newSearch);
    },
    page() {
      this.getCategories(this.search);
    },
  },
  methods: {
    async getCategories(search = "") {
      this.loading = true;
      try {
        const response = await ProductCategoryService.getCategoryList(
          search,
          this.page
        );
        this.categories = response.data?.data || [];
        this.total = response.data?.meta?.last_page || 0;
        this.totalCategories = response.data?.meta?.total || 0;
      } catch (error) {
        this.categories = [];
        this.total = 0;
        this.totalCategories = 0;
        console.error("Error fetching Category List:", error);
      } finally {
        this.loading = false;
      }
    },
    dropCategoryConfirm(uuid) {
      this.dropId = uuid;
      this.confirmOpen = true;
    },
    dropCategory() {
      if (this.dropId) {
        this.loading = true;
        ProductCategoryService.dropCategory(this.dropId)
          .then((response) => {
            if (
              response.data.message === "Product category deleted successfully"
            ) {
              this.loading = false;
              this.getCategories(this.search);
              this.snackbarColor = "success";
              this.successMsg = "Category dropped successfully";
            }
            this.snackbar = true;
          })
          .catch((error) => {
            this.loading = false;
            let data = error.response?.data?.message || "Unknown error";
            let errorOutput = checkError.errorFormatting(data);
            let errorsArray = [];
            if (Array.isArray(errorOutput)) {
              errorsArray = errorOutput.filter((e) => !!e && e.trim());
              if (errorsArray.length === 0) errorsArray = [data];
            } else if (errorOutput && errorOutput.trim()) {
              errorsArray = [errorOutput];
            } else {
              errorsArray = [data];
            }
            this.errors = errorsArray;
          });
      }
    },
    stringFormatting(str) {
      return str.replace(/_/g, " ");
    },
    getStatusColor(status) {
      let bgColor = "";
      switch (true) {
        case status === "ACTIVE":
          bgColor = "#A5D6A7";
          break;
        default:
          bgColor = "#EF9A9A";
          break;
      }
      return bgColor;
    },
    searchChange(event) {
      this.search = event;
    },
    cancelConfirm() {
      this.confirmOpen = false;
      console.log("Cancel product");
    },
    confirm() {
      this.confirmOpen = false;
      this.dropCategory();
    },

    closeSnackbar() {
      this.snackbar = false;
    },

    onDismissed() {
      this.errors = null;
    },
  },
};
</script>
